import React, { useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';

interface CVAutocompleteProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  onAddToCV?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

export const CVAutocomplete = ({
  label,
  value,
  options,
  onChange,
  onAddToCV,
  placeholder,
  disabled = false,
  isLoading = false,
}: CVAutocompleteProps) => {
  // Check if current value matches any option (case-insensitive)
  const valueExistsInOptions = useMemo(() => {
    if (!value || !value.trim()) return true; // Empty value doesn't need "Add to CV"
    return options.some(
      (option) => option.toLowerCase() === value.toLowerCase()
    );
  }, [value, options]);

  const trimmedValue = value.trim();
  const addToCVDisabled = !trimmedValue || valueExistsInOptions;
  const showAddToCV = Boolean(onAddToCV);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: string | null
  ) => {
    onChange(newValue || '');
  };

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
    reason: string
  ) => {
    // Update value on input change for freeSolo mode
    if (reason === 'input') {
      onChange(newInputValue);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Ensure the current input value is saved on blur
    const inputValue = event.target.value;
    if (inputValue !== value) {
      onChange(inputValue);
    }
  };

  const handleAddToCV = () => {
    if (onAddToCV && value.trim()) {
      onAddToCV(value.trim());
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
      <Autocomplete
        freeSolo
        value={value}
        onChange={handleChange}
        onInputChange={handleInputChange}
        options={options}
        disabled={disabled}
        loading={isLoading}
        sx={{ flexGrow: 1 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            onBlur={handleBlur}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        filterOptions={(options, { inputValue }) => {
          // Filter options based on input
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(inputValue.toLowerCase())
          );
          return filtered;
        }}
      />
      {showAddToCV && (
        <Chip
          icon={<Add />}
          label="Add to CV"
          onClick={addToCVDisabled ? undefined : handleAddToCV}
          color="primary"
          variant="outlined"
          disabled={addToCVDisabled}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
};
